import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, parentApi } from '../../api/client.js';
import type { ChildProfile, InternetAccessAccount } from '../../api/types.js';
import {
  internetEndedNotification,
  internetStartedNotification,
  requestNotificationPermission,
  showNotification,
} from '../../core/notifications.js';
import type { ParentDashboardState } from './state.js';
import { initialParentDashboardState, withoutKey } from './state.js';

/**
 * Port of the Android app's `ParentDashboardViewModel`. Every action there
 * has a same-named counterpart here, with the same optimistic-update and
 * error-message behaviour, so the two clients stay easy to reason about
 * together.
 *
 * The one thing this has that the Android version does not is the
 * transition-detecting notification logic — see `core/notifications.ts`
 * for why the web app derives those from the poll instead of subscribing
 * to the backend's UnifiedPush sender.
 */

/** Matches `ParentDashboardScreen`'s poll loop. */
const POLL_INTERVAL_MS = 5_000;

/** The step size the UI offers, enforced server-side too (multiples of 5). */
const TOP_UP_MINUTES = 5;

export interface ParentDashboardActions {
  refresh: () => void;
  onNewChildNameChange: (value: string) => void;
  createChildProfile: () => void;
  deleteChildProfile: (childProfileId: string) => void;
  generatePairingCode: (childProfileId: string) => void;
  cancelPairingCode: (childProfileId: string) => void;
  revokeDevice: (childProfileId: string, childDeviceId: string) => void;
  topUpInternetAccess: (childProfileId: string) => void;
  deductBonusInternetAccess: (childProfileId: string) => void;
  setForceBlocked: (childProfileId: string, forceBlocked: boolean) => void;
  resetInternetAccess: (childProfileId: string) => void;
  addBlockWindow: (childProfileId: string, startTime: string, endTime: string) => void;
  removeBlockWindow: (childProfileId: string, blockWindowId: string) => void;
  dismissError: () => void;
}

export function useParentDashboard(onSessionExpired: () => void): {
  state: ParentDashboardState;
  actions: ParentDashboardActions;
} {
  const [state, setState] = useState<ParentDashboardState>(initialParentDashboardState);

  // Latest state, readable from callbacks and the poll timer without
  // making them depend on it. Deliberately *not* read inside a `setState`
  // updater: those must stay pure, or React's StrictMode double-invoke
  // would fire every request in here twice in development.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Kept in a ref, not state: these exist only to compare one poll against
  // the previous one, and re-rendering when they change would be pointless
  // churn. `undefined` for a child means "no baseline yet" — the first
  // observation must never fire a notification, or every page load would
  // announce whoever happens to be online right now.
  const previousOnlineByChildId = useRef<Record<string, boolean>>({});
  const childNamesById = useRef<Record<string, string>>({});

  // A 401 must log the whole app out, but every request could produce one,
  // so it is handled in exactly one place. Held in a ref so the callbacks
  // below don't all need to be rebuilt when the parent re-renders.
  const onSessionExpiredRef = useRef(onSessionExpired);
  onSessionExpiredRef.current = onSessionExpired;

  /**
   * Runs one API call, routing a dead session to logout and anything else
   * to the dashboard's error banner. Returns `null` on failure so callers
   * can simply bail.
   */
  const run = useCallback(async <T,>(call: () => Promise<T>, fallbackMessage: string): Promise<T | null> => {
    try {
      return await call();
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthenticated) {
        onSessionExpiredRef.current();
        return null;
      }
      const message = error instanceof ApiError ? error.message : fallbackMessage;
      setState((current) => ({ ...current, errorMessage: message }));
      return null;
    }
  }, []);

  /**
   * Re-polls every child's balance without touching anything else (child
   * profiles, devices, pairing codes) — unlike `refresh`, this doesn't
   * flip `isLoading`, so it's safe to call on a timer while the dashboard
   * is visible without flashing the spinner every few seconds.
   *
   * Also where the "child started/ended internet" notifications come
   * from: `onlineDeviceId` crossing null in either direction is exactly
   * the event the Android app receives as a push.
   */
  const loadInternetAccessAccounts = useCallback(
    async (profiles: ChildProfile[]) => {
      const results = await Promise.all(
        profiles.map(async (profile) => {
          try {
            const account = await parentApi.getInternetAccessAccount(profile.id);
            return [profile.id, account] as const;
          } catch (error) {
            if (error instanceof ApiError && error.isUnauthenticated) {
              onSessionExpiredRef.current();
            }
            // Any other per-child failure is skipped silently, exactly
            // like the Android version's `result.getOrNull()` — one
            // child's hiccup must not blank out the others' balances.
            return null;
          }
        }),
      );

      const loaded = results.filter((entry): entry is readonly [string, InternetAccessAccount] => entry !== null);

      for (const [childProfileId, account] of loaded) {
        const isOnline = account.onlineDeviceId !== null;
        const wasOnline = previousOnlineByChildId.current[childProfileId];
        previousOnlineByChildId.current[childProfileId] = isOnline;

        if (wasOnline === undefined || wasOnline === isOnline) continue;
        const childName = childNamesById.current[childProfileId] ?? 'Ihr Kind';
        showNotification(
          isOnline ? internetStartedNotification(childName) : internetEndedNotification(childName),
        );
      }

      if (loaded.length === 0) return;
      setState((current) => ({
        ...current,
        internetAccessAccountByChildId: {
          ...current.internetAccessAccountByChildId,
          ...Object.fromEntries(loaded),
        },
      }));
    },
    [],
  );

  const loadDevices = useCallback(async (profiles: ChildProfile[]) => {
    const results = await Promise.all(
      profiles.map(async (profile) => {
        const devices = await parentApi.listDevices(profile.id).catch(() => []);
        return [profile.id, devices] as const;
      }),
    );
    setState((current) => ({ ...current, devicesByChildId: Object.fromEntries(results) }));
  }, []);

  /**
   * Reloads child profiles and, for each, its paired devices and balance.
   * There's no push mechanism to notice a newly-redeemed pairing code on
   * another device, so this is also what the manual "Aktualisieren"
   * button does.
   */
  const refresh = useCallback(() => {
    setState((current) => ({ ...current, isLoading: true, errorMessage: null }));
    void (async () => {
      const profiles = await run(
        () => parentApi.listChildProfiles(),
        'Kinderprofile konnten nicht geladen werden',
      );
      if (!profiles) {
        setState((current) => ({ ...current, isLoading: false }));
        return;
      }

      childNamesById.current = Object.fromEntries(profiles.map((p) => [p.id, p.displayName]));
      setState((current) => ({ ...current, isLoading: false, childProfiles: profiles }));
      await Promise.all([loadDevices(profiles), loadInternetAccessAccounts(profiles)]);
    })();
  }, [loadDevices, loadInternetAccessAccounts, run]);

  useEffect(() => {
    void requestNotificationPermission();
    refresh();
  }, [refresh]);

  // Keeps each child's balance live while the dashboard is open — the same
  // "authenticated polling is the source of truth, no push dependency"
  // principle the Android screens follow. Reads the profile list out of
  // state via the setter so the interval never has to be torn down and
  // rebuilt when a child is added or removed.
  useEffect(() => {
    const interval = setInterval(() => {
      const { childProfiles } = stateRef.current;
      if (childProfiles.length > 0) void loadInternetAccessAccounts(childProfiles);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadInternetAccessAccounts]);

  // -- Child profiles ------------------------------------------------------

  const onNewChildNameChange = useCallback((value: string) => {
    setState((current) => ({ ...current, newChildName: value }));
  }, []);

  const createChildProfile = useCallback(() => {
    const name = stateRef.current.newChildName.trim();
    if (name === '' || stateRef.current.isCreatingChild) return;

    setState((current) => ({ ...current, isCreatingChild: true }));
    void (async () => {
      const created = await run(
        () => parentApi.createChildProfile(name),
        'Kinderprofil konnte nicht erstellt werden',
      );
      if (!created) {
        setState((current) => ({ ...current, isCreatingChild: false }));
        return;
      }
      childNamesById.current[created.id] = created.displayName;
      setState((current) => ({
        ...current,
        isCreatingChild: false,
        newChildName: '',
        childProfiles: [...current.childProfiles, created],
        devicesByChildId: { ...current.devicesByChildId, [created.id]: [] },
      }));
      void loadInternetAccessAccounts([created]);
    })();
  }, [loadInternetAccessAccounts, run]);

  /**
   * Permanently deletes a child — irreversible; everything cascaded from
   * it on the backend (devices, pairing codes, Internet-Access balance,
   * usage history, ...) goes too. The confirmation dialog lives in the
   * view; by the time this runs the parent has already confirmed.
   */
  const deleteChildProfile = useCallback(
    (childProfileId: string) => {
      setState((current) => ({ ...current, deletingChildProfileId: childProfileId }));
      void (async () => {
        const result = await run(
          () => parentApi.deleteChildProfile(childProfileId),
          'Kind konnte nicht gelöscht werden',
        );
        if (result === null) {
          setState((current) => ({ ...current, deletingChildProfileId: null }));
          return;
        }
        delete previousOnlineByChildId.current[childProfileId];
        delete childNamesById.current[childProfileId];
        setState((current) => ({
          ...current,
          deletingChildProfileId: null,
          childProfiles: current.childProfiles.filter((child) => child.id !== childProfileId),
          devicesByChildId: withoutKey(current.devicesByChildId, childProfileId),
          pairingCodesByChildId: withoutKey(current.pairingCodesByChildId, childProfileId),
          internetAccessAccountByChildId: withoutKey(
            current.internetAccessAccountByChildId,
            childProfileId,
          ),
        }));
      })();
    },
    [run],
  );

  // -- Pairing -------------------------------------------------------------

  const generatePairingCode = useCallback(
    (childProfileId: string) => {
      setState((current) => ({ ...current, pendingPairingCodeChildId: childProfileId }));
      void (async () => {
        const pairingCode = await run(
          () => parentApi.createPairingCode(childProfileId),
          'Pairing-Code konnte nicht erstellt werden',
        );
        setState((current) => ({
          ...current,
          pendingPairingCodeChildId: null,
          pairingCodesByChildId: pairingCode
            ? { ...current.pairingCodesByChildId, [childProfileId]: pairingCode }
            : current.pairingCodesByChildId,
        }));
      })();
    },
    [run],
  );

  /**
   * Cancels a pairing code — offered for the whole time it's displayed,
   * not just while it's still valid, so an already-expired code that's
   * merely still shown has a way to be dismissed instead of only going
   * away on a manual refresh, a reload, or a freshly generated code.
   */
  const cancelPairingCode = useCallback(
    (childProfileId: string) => {
      const pairingCode = stateRef.current.pairingCodesByChildId[childProfileId];
      if (!pairingCode) return;

      setState((current) => ({ ...current, cancellingPairingCodeChildId: childProfileId }));
      void (async () => {
        const result = await run(
          () => parentApi.cancelPairingCode(childProfileId, pairingCode.id),
          'Pairing-Code konnte nicht abgebrochen werden',
        );
        setState((current) => ({
          ...current,
          cancellingPairingCodeChildId: null,
          pairingCodesByChildId:
            result === null
              ? current.pairingCodesByChildId
              : withoutKey(current.pairingCodesByChildId, childProfileId),
        }));
      })();
    },
    [run],
  );

  /**
   * Unpairs one of a child's devices — the primary unpair path, driven
   * from the parent side. Requires no access to the device in question.
   */
  const revokeDevice = useCallback(
    (childProfileId: string, childDeviceId: string) => {
      setState((current) => ({ ...current, revokingDeviceId: childDeviceId }));
      void (async () => {
        const result = await run(
          () => parentApi.revokeDevice(childDeviceId),
          'Gerät konnte nicht entkoppelt werden',
        );
        setState((current) => ({
          ...current,
          revokingDeviceId: null,
          devicesByChildId:
            result === null
              ? current.devicesByChildId
              : {
                  ...current.devicesByChildId,
                  [childProfileId]: (current.devicesByChildId[childProfileId] ?? []).filter(
                    (device) => device.id !== childDeviceId,
                  ),
                },
        }));
      })();
    },
    [run],
  );

  // -- Internet-Access -----------------------------------------------------

  /**
   * The balance actions share a shape: flip one "saving" marker,
   * call, and fold the returned account back in. Factored out rather than
   * written four times, unlike the Kotlin original where each is its own
   * method — the duplication there buys nothing here.
   */
  const runAccountAction = useCallback(
    (
      childProfileId: string,
      savingKey: keyof Pick<
        ParentDashboardState,
        | 'savingTopUpChildId'
        | 'savingDeductBonusChildId'
        | 'savingForceBlockedChildId'
        | 'savingResetChildId'
        | 'savingBlockWindowChildId'
      >,
      call: () => Promise<InternetAccessAccount>,
      fallbackMessage: string,
    ) => {
      setState((current) => ({ ...current, [savingKey]: childProfileId }));
      void (async () => {
        const account = await run(call, fallbackMessage);
        setState((current) => ({
          ...current,
          [savingKey]: null,
          internetAccessAccountByChildId: account
            ? { ...current.internetAccessAccountByChildId, [childProfileId]: account }
            : current.internetAccessAccountByChildId,
        }));
      })();
    },
    [run],
  );

  /** Adds 5 minutes to one child's bonus pot. */
  const topUpInternetAccess = useCallback(
    (childProfileId: string) =>
      runAccountAction(
        childProfileId,
        'savingTopUpChildId',
        () => parentApi.topUpInternetAccess(childProfileId, TOP_UP_MINUTES),
        'Guthaben konnte nicht aufgeladen werden',
      ),
    [runAccountAction],
  );

  /** Mirror of the top-up — removes 5 minutes from the bonus pot only, clamped at zero server-side. */
  const deductBonusInternetAccess = useCallback(
    (childProfileId: string) =>
      runAccountAction(
        childProfileId,
        'savingDeductBonusChildId',
        () => parentApi.deductBonusInternetAccess(childProfileId, TOP_UP_MINUTES),
        'Bonus konnte nicht abgezogen werden',
      ),
    [runAccountAction],
  );

  /** Override (e.g. "Hausaufgaben") — cuts off internet immediately, regardless of balance. */
  const setForceBlocked = useCallback(
    (childProfileId: string, forceBlocked: boolean) =>
      runAccountAction(
        childProfileId,
        'savingForceBlockedChildId',
        () => parentApi.setForceBlocked(childProfileId, forceBlocked),
        'Sperre konnte nicht gespeichert werden',
      ),
    [runAccountAction],
  );

  /**
   * Hard reset to the standard daily allowance — discards whatever was
   * left over or overdrawn today, rather than adding to it like the
   * top-up. See docs/INTERNET_ACCESS.md.
   */
  const resetInternetAccess = useCallback(
    (childProfileId: string) =>
      runAccountAction(
        childProfileId,
        'savingResetChildId',
        () => parentApi.resetInternetAccess(childProfileId),
        'Guthaben konnte nicht zurückgesetzt werden',
      ),
    [runAccountAction],
  );

  /** Fixed daily Sperrzeit — the backend refuses activation inside it and ends open sessions when it begins. */
  const addBlockWindow = useCallback(
    (childProfileId: string, startTime: string, endTime: string) =>
      runAccountAction(
        childProfileId,
        'savingBlockWindowChildId',
        () => parentApi.addBlockWindow(childProfileId, startTime, endTime),
        'Sperrzeit konnte nicht gespeichert werden',
      ),
    [runAccountAction],
  );

  const removeBlockWindow = useCallback(
    (childProfileId: string, blockWindowId: string) =>
      runAccountAction(
        childProfileId,
        'savingBlockWindowChildId',
        () => parentApi.removeBlockWindow(childProfileId, blockWindowId),
        'Sperrzeit konnte nicht entfernt werden',
      ),
    [runAccountAction],
  );

  const dismissError = useCallback(() => {
    setState((current) => ({ ...current, errorMessage: null }));
  }, []);

  return {
    state,
    actions: {
      refresh,
      onNewChildNameChange,
      createChildProfile,
      deleteChildProfile,
      generatePairingCode,
      cancelPairingCode,
      revokeDevice,
      topUpInternetAccess,
      deductBonusInternetAccess,
      setForceBlocked,
      resetInternetAccess,
      addBlockWindow,
      removeBlockWindow,
      dismissError,
    },
  };
}
